import _taggedTemplateLiteral from '@babel/runtime/helpers/esm/taggedTemplateLiteral';
import { css, keyframes, useTheme, jsx } from '@emotion/react';
import { math, stripUnit, remToPx } from 'polished';
import { textStyles } from '@paypalcorp/pp-react-text';
import { devWarning, utilStyles, getColorValue, ColorContext, hasWindow } from '@paypalcorp/pp-react-utils';
import _defineProperty from '@babel/runtime/helpers/esm/defineProperty';
import _extends from '@babel/runtime/helpers/esm/extends';
import _objectWithoutProperties from '@babel/runtime/helpers/esm/objectWithoutProperties';
import React, { forwardRef, useMemo, useState, useRef, Children } from 'react';
import { LoadingSpinner } from '@paypalcorp/pp-react-loading-spinner';
import { CheckmarkIcon, ChevronUpIcon, ChevronDownIcon } from '@paypalcorp/pp-react-icons';
import _slicedToArray from '@babel/runtime/helpers/esm/slicedToArray';
import { ActionMenu } from '@paypalcorp/pp-react-menu';

var _templateObject, _templateObject2, _templateObject3, _templateObject4, _templateObject5, _templateObject6, _templateObject7, _templateObject8, _templateObject9, _templateObject10, _templateObject11, _templateObject12, _templateObject13, _templateObject14, _templateObject15, _templateObject16, _templateObject17, _templateObject18, _templateObject19, _templateObject20, _templateObject21;
function styles(tokens, colorTokens) {
  /* istanbul ignore next */
  if (!Object.keys(tokens).length) {
    devWarning('Missing Emotion theme. Use ThemeProvider from @emotion/react to pass a valid theme from pp-react.');
    return {};
  }

  var sysSpaceSm = tokens.sysSpaceSm,
      sysSpaceMd = tokens.sysSpaceMd,
      sysSpaceLg = tokens.sysSpaceLg,
      sysSpaceJumbo = tokens.sysSpaceJumbo,
      sysBorderRadiusSm = tokens.sysBorderRadiusSm,
      sysBorderThicknessEmphasis = tokens.sysBorderThicknessEmphasis,
      sysSizeIndicatorMd = tokens.sysSizeIndicatorMd,
      sysMotionDurationAction = tokens.sysMotionDurationAction,
      sysColorPrimaryMain = tokens.sysColorPrimaryMain,
      sysColorPrimaryContrast = tokens.sysColorPrimaryContrast,
      sysColorPrimaryMainHover = tokens.sysColorPrimaryMainHover,
      sysColorPrimaryMainActive = tokens.sysColorPrimaryMainActive,
      sysColorPrimaryContrastHover = tokens.sysColorPrimaryContrastHover,
      sysColorPrimaryContrastActive = tokens.sysColorPrimaryContrastActive,
      sysColorFeaturedMain = tokens.sysColorFeaturedMain,
      sysColorFeaturedMainHover = tokens.sysColorFeaturedMainHover,
      sysColorFeaturedMainActive = tokens.sysColorFeaturedMainActive,
      sysColorFeaturedContrast = tokens.sysColorFeaturedContrast,
      sysColorSecondaryMain = tokens.sysColorSecondaryMain,
      sysColorSuccessMain = tokens.sysColorSuccessMain,
      sysMotionDurationHover = tokens.sysMotionDurationHover,
      sysSizeMediaMd = tokens.sysSizeMediaMd,
      sysSizeMediaSm = tokens.sysSizeMediaSm;

  var _utilStyles = utilStyles(tokens),
      screenReader = _utilStyles.screenReader,
      focusStyles = _utilStyles.focusStyles;

  var _textStyles = textStyles(tokens),
      buttonLg = _textStyles.buttonLg,
      buttonSm = _textStyles.buttonSm;

  var focusOffsetElement = focusStyles.focusOffsetElement; // Base Styles for button

  var base = css(_templateObject || (_templateObject = _taggedTemplateLiteral(["\n    label: button_base;\n    position: relative;\n    border-radius: 1000px;\n    color: ", ";\n    cursor: pointer;\n    display: inline-block;\n    min-width: ", ";\n    text-align: center;\n    text-decoration: none;\n    transition: color ", " ease,\n      background-color ", " ease,\n      border-color ", " ease;\n    border: ", " solid\n      ", ";\n    ", ";\n    background-color: ", ";\n    padding: ", "\n      ", ";\n\n    &:hover,\n    &:active,\n    &:visited {\n      color: ", ";\n    }\n\n    &:hover {\n      text-decoration: none;\n      background-color: ", ";\n      border-color: ", ";\n    }\n\n    &:active {\n      outline: none;\n      background-color: ", ";\n      border-color: ", ";\n    }\n\n    &:focus {\n      outline: none;\n      ", ";\n    }\n  "])), getColorValue(colorTokens, sysColorPrimaryContrast, 'contrast'), '6rem', sysMotionDurationAction, sysMotionDurationAction, sysMotionDurationAction, sysBorderThicknessEmphasis, getColorValue(colorTokens, sysColorPrimaryMain, 'main'), buttonLg({
    color: getColorValue(colorTokens, sysColorPrimaryContrast, 'contrast')
  }), getColorValue(colorTokens, sysColorPrimaryMain, 'main'), math("".concat(sysSpaceMd, " - ").concat(sysBorderThicknessEmphasis)), math("".concat(sysSpaceJumbo, " - ").concat(sysBorderThicknessEmphasis)), getColorValue(colorTokens, sysColorPrimaryContrast, 'contrast'), getColorValue(colorTokens, sysColorPrimaryMainHover, 'hover'), getColorValue(colorTokens, sysColorPrimaryMainHover, 'hover'), getColorValue(colorTokens, sysColorPrimaryMainActive, 'active'), getColorValue(colorTokens, sysColorPrimaryMainActive, 'active'), focusOffsetElement); // Styles for button in dark background

  var inverse = css(_templateObject2 || (_templateObject2 = _taggedTemplateLiteral(["\n    label: inverse;\n    background-color: ", ";\n    border-color: ", ";\n    color: ", ";\n\n    &:hover,\n    &:active,\n    &:visited {\n      color: ", ";\n    }\n\n    &:hover {\n      background: ", ";\n      border-color: ", ";\n    }\n\n    &:active {\n      background: ", ";\n      border-color: ", ";\n    }\n\n    &:focus {\n      border: 2px solid ", ";\n    }\n  "])), getColorValue(colorTokens, sysColorPrimaryContrast, 'contrast'), getColorValue(colorTokens, sysColorPrimaryContrast, 'contrast'), getColorValue(colorTokens, sysColorPrimaryMain, 'main'), getColorValue(colorTokens, sysColorPrimaryMain, 'main'), getColorValue(colorTokens, sysColorPrimaryContrastHover, 'hover'), getColorValue(colorTokens, sysColorPrimaryContrastHover, 'hover'), getColorValue(colorTokens, sysColorPrimaryContrastActive, 'active'), getColorValue(colorTokens, sysColorPrimaryContrastActive, 'active'), sysColorPrimaryMain); // Styles for secondary buttons

  var secondary = css(_templateObject3 || (_templateObject3 = _taggedTemplateLiteral(["\n    label: secondary;\n    background: transparent;\n    border-color: ", ";\n    color: ", ";\n\n    &:visited {\n      color: ", ";\n    }\n\n    &:hover {\n      background: transparent;\n      color: ", ";\n      border-color: ", ";\n    }\n\n    &:active {\n      background: transparent;\n      color: ", ";\n      border-color: ", ";\n    }\n  "])), getColorValue(colorTokens, sysColorPrimaryMain, 'main'), getColorValue(colorTokens, sysColorPrimaryMain, 'main'), getColorValue(colorTokens, sysColorPrimaryMain, 'main'), getColorValue(colorTokens, sysColorPrimaryMainHover, 'hover'), getColorValue(colorTokens, sysColorPrimaryMainHover, 'hover'), getColorValue(colorTokens, sysColorPrimaryMainActive, 'active'), getColorValue(colorTokens, sysColorPrimaryMainActive, 'active')); // Styles for secondary inverse

  var secondaryInverse = css(_templateObject4 || (_templateObject4 = _taggedTemplateLiteral(["\n    label: secondary_inverse;\n    border-color: ", ";\n    color: ", ";\n    background-color: transparent;\n\n    &:visited {\n      color: ", ";\n    }\n\n    &:hover {\n      background: ", ";\n      color: ", ";\n      border-color: ", ";\n    }\n\n    &:active {\n      background: ", ";\n      color: ", ";\n      border-color: ", ";\n    }\n\n    &:focus {\n      border: 2px solid ", ";\n    }\n  "])), getColorValue(colorTokens, sysColorPrimaryContrast, 'contrast'), getColorValue(colorTokens, sysColorPrimaryContrast, 'contrast'), getColorValue(colorTokens, sysColorPrimaryContrastActive, 'contrast'), getColorValue(colorTokens, 'transparent', 'hover'), getColorValue(colorTokens, sysColorPrimaryContrastHover, 'contrast'), getColorValue(colorTokens, sysColorPrimaryContrastHover, 'contrast'), getColorValue(colorTokens, sysColorPrimaryMainActive, 'active'), getColorValue(colorTokens, sysColorPrimaryContrastActive, 'contrast'), getColorValue(colorTokens, sysColorPrimaryContrastActive, 'contrast'), sysColorPrimaryMain); // Styles for branded buttons
  // Only applies to large variant, cannot be applied to icon

  var branded = css(_templateObject5 || (_templateObject5 = _taggedTemplateLiteral(["\n    label: branded;\n    background: ", ";\n    border-color: ", ";\n    color: ", ";\n\n    &:hover,\n    &:active,\n    &:visited {\n      color: ", ";\n    }\n\n    &:hover {\n      background: ", ";\n      border-color: ", ";\n    }\n\n    &:active {\n      background: ", ";\n      border-color: ", ";\n    }\n  "])), sysColorFeaturedMain, sysColorFeaturedMain, sysColorFeaturedContrast, sysColorFeaturedContrast, sysColorFeaturedMainHover, sysColorFeaturedMainHover, sysColorFeaturedMainActive, sysColorFeaturedMainActive); // Styles for tertiary button

  var tertiary = css(_templateObject6 || (_templateObject6 = _taggedTemplateLiteral(["\n    label: tertiary;\n    position: relative;\n    cursor: pointer;\n    display: inline-block;\n    background: transparent;\n    border: none;\n    ", "\n    padding: ", " ", ";\n\n    &:hover,\n    &:active {\n      color: ", ";\n      background: transparent;\n    }\n\n    &:hover,\n    &:focus {\n      text-decoration: underline;\n    }\n\n    &:active {\n      text-decoration: none;\n    }\n\n    &:focus {\n      color: ", ";\n      border-radius: ", ";\n      ", ";\n    }\n  "])), buttonLg({
    color: getColorValue(colorTokens, sysColorSecondaryMain, 'main')
  }), sysSpaceMd, sysSpaceSm, getColorValue(colorTokens, sysColorSecondaryMain, 'main'), getColorValue(colorTokens, sysColorSecondaryMain, 'main'), sysBorderRadiusSm, focusOffsetElement); // styles for icon

  var icon = css(_templateObject7 || (_templateObject7 = _taggedTemplateLiteral(["\n    label: icon;\n    vertical-align: top;\n    margin-inline-end: ", ";\n    margin-inline-start: 0;\n  "])), sysSpaceSm);
  var affordance = css(_templateObject8 || (_templateObject8 = _taggedTemplateLiteral(["\n    label: affordance;\n    margin-inline-start: ", ";\n    margin-inline-end: 0;\n    vertical-align: top;\n    position: relative;\n    pointer-events: none;\n  "])), sysSpaceSm);
  var menuWrapper = css(_templateObject9 || (_templateObject9 = _taggedTemplateLiteral(["\n    label: menu-wrap;\n    position: relative;\n  "]))); // Size sm

  var sizeSm = css(_templateObject10 || (_templateObject10 = _taggedTemplateLiteral(["\n    label: size_sm;\n    ", "\n    min-width: ", ";\n    padding: ", "\n      ", ";\n  "])), buttonSm({
    color: getColorValue(colorTokens, sysColorPrimaryContrast, 'contrast')
  }), '3.75rem', math("".concat(sysSizeIndicatorMd, " - ").concat(sysBorderThicknessEmphasis)), math("".concat(sysSpaceLg, " - ").concat(sysBorderThicknessEmphasis))); // Stateful button styles
  // Button success state

  var successState = css(_templateObject11 || (_templateObject11 = _taggedTemplateLiteral(["\n    label: success_state;\n    border-color: ", ";\n    background-color: ", ";\n    pointer-events: none;\n  "])), getColorValue(colorTokens, sysColorSuccessMain, 'main'), getColorValue(colorTokens, sysColorSuccessMain, 'main')); // Button processing state

  var processingState = css(_templateObject12 || (_templateObject12 = _taggedTemplateLiteral(["\n    label: processing_state;\n    color: transparent;\n    pointer-events: none;\n\n    &:hover,\n    &:active,\n    &:visited {\n      color: transparent;\n    }\n  "])));
  var fadeIn = keyframes(_templateObject13 || (_templateObject13 = _taggedTemplateLiteral(["\n    0% {\n      opacity: 0;\n    }\n    100% {\n      opacity: 1;\n    }\n  "]))); //Overlay

  var overlay = css(_templateObject14 || (_templateObject14 = _taggedTemplateLiteral(["\n    label: overlay;\n    position: absolute;\n    top: 0;\n    bottom: 0;\n    left: 0;\n    right: 0;\n    border-radius: inherit;\n    text-align: center;\n    opacity: 0;\n    animation: ", " ", " ease-in forwards;\n    display: flex;\n    justify-content: center;\n    align-items: center;\n    padding-right: 0;\n  "])), fadeIn, sysMotionDurationHover);
  var successStateOverlay = css(_templateObject15 || (_templateObject15 = _taggedTemplateLiteral(["\n    label: success_state_overlay;\n    background-color: ", ";\n  "])), getColorValue(colorTokens, sysColorSuccessMain, 'main'));
  var successStateIcon = css(_templateObject16 || (_templateObject16 = _taggedTemplateLiteral(["\n    label: success_state_icon;\n    line-height: ", ";\n    color: ", ";\n  "])), 2, getColorValue(colorTokens, sysColorPrimaryContrast, 'contrast'));
  var successStateIconSm = css(_templateObject17 || (_templateObject17 = _taggedTemplateLiteral(["\n    label: success_state_icon_sm;\n    line-height: ", ";\n  "])), 1.6);
  var buttonGroup = css(_templateObject18 || (_templateObject18 = _taggedTemplateLiteral(["\n    label: button_group;\n    display: inline-grid;\n    grid-template-columns: 1fr 1fr;\n    gap: ", ";\n    padding: ", " ", ";\n  "])), sysSpaceLg, sysSpaceMd, sysSpaceLg);
  var buttonGroupSolo = css(_templateObject19 || (_templateObject19 = _taggedTemplateLiteral(["\n    label: solo;\n    grid-template-columns: 1fr;\n  "])));
  var btnFullWidth = css(_templateObject20 || (_templateObject20 = _taggedTemplateLiteral(["\n    label: btn_full_width;\n    @media (max-width: ", ") {\n      width: 100%;\n    }\n  "])), sysSizeMediaMd);
  var btnGroupFullWidth = css(_templateObject21 || (_templateObject21 = _taggedTemplateLiteral(["\n    label: btn_group_full_width;\n    @media (max-width: ", ") {\n      grid-template-columns: none;\n      width: 100%;\n    }\n  "])), sysSizeMediaSm);
  return {
    base: base,
    secondary: secondary,
    inverse: inverse,
    secondaryInverse: secondaryInverse,
    branded: branded,
    tertiary: tertiary,
    icon: icon,
    affordance: affordance,
    menuWrapper: menuWrapper,
    sizeSm: sizeSm,
    successState: successState,
    processingState: processingState,
    screenReader: screenReader,
    overlay: overlay,
    successStateOverlay: successStateOverlay,
    successStateIcon: successStateIcon,
    successStateIconSm: successStateIconSm,
    buttonGroup: buttonGroup,
    buttonGroupSolo: buttonGroupSolo,
    btnFullWidth: btnFullWidth,
    btnGroupFullWidth: btnGroupFullWidth
  };
}

var _excluded = ["isMenu", "isOpen", "isSmall"],
    _excluded2 = ["size", "inverse", "secondary", "branded", "tertiary", "isMenu", "isOpen", "btnState", "screenReaderText", "className", "children", "as", "iconComponent", "fullWidthOnMobile"];

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

var AffordanceIcon = function (_ref) {
  var isMenu = _ref.isMenu,
      isOpen = _ref.isOpen,
      isSmall = _ref.isSmall,
      rest = _objectWithoutProperties(_ref, _excluded);

  if (!isMenu) {
    return null;
  }

  var Icon = isOpen ? ChevronUpIcon : ChevronDownIcon; // @ts-ignore

  return jsx(Icon, _extends({}, rest, {
    size: isSmall ? 'xs' : 'sm',
    "data-ppui": "true"
  }));
};

/* eslint-disable complexity */
var Button = forwardRef(function (_ref2, ref) {
  var size = _ref2.size,
      inverse = _ref2.inverse,
      secondary = _ref2.secondary,
      branded = _ref2.branded,
      tertiary = _ref2.tertiary,
      isMenu = _ref2.isMenu,
      isOpen = _ref2.isOpen,
      _ref2$btnState = _ref2.btnState,
      btnState = _ref2$btnState === void 0 ? 'initial' : _ref2$btnState,
      screenReaderText = _ref2.screenReaderText,
      className = _ref2.className,
      children = _ref2.children,
      as = _ref2.as,
      iconComponent = _ref2.iconComponent,
      _ref2$fullWidthOnMobi = _ref2.fullWidthOnMobile,
      fullWidthOnMobile = _ref2$fullWidthOnMobi === void 0 ? true : _ref2$fullWidthOnMobi,
      rest = _objectWithoutProperties(_ref2, _excluded2);

  // If theme is not directly provided, get from themeContext
  var tokens = useTheme();
  var colorTokens = React.useContext(ColorContext);
  var buttonStyles = useMemo(function () {
    return styles(tokens, colorTokens);
  }, [tokens]);
  var loadingSpinnerProps = {};

  if (btnState === 'processing') {
    loadingSpinnerProps.as = 'span';

    if (size === 'sm') {
      loadingSpinnerProps.size = 'xs';
    }

    if (secondary && inverse || !secondary && !inverse) {
      loadingSpinnerProps.inverse = true;
    }
  }

  var cssVal;

  if (branded) {
    cssVal = [buttonStyles.base, buttonStyles.branded];
  } else if (tertiary) {
    cssVal = [buttonStyles.tertiary];
  } else {
    // Note: the order of composition below is very important since rules from a latter style set will override the former.
    cssVal = [buttonStyles.base, fullWidthOnMobile && buttonStyles.btnFullWidth, size === 'sm' && buttonStyles.sizeSm, inverse && buttonStyles.inverse, secondary && buttonStyles.secondary, secondary && inverse && buttonStyles.secondaryInverse, btnState === 'success' && buttonStyles.successState, btnState === 'processing' && buttonStyles.processingState];
  }

  var componentProps = _objectSpread({}, rest);

  if (isOpen !== undefined) {
    componentProps['aria-expanded'] = isOpen;
  }

  return jsx(as || 'button', _extends({
    css: cssVal,
    className: className,
    disabled: btnState && btnState !== 'initial',
    ref: ref,
    "data-ppui-info": "buttons_7.0.0-ql"
  }, componentProps), function renderChildren() {
    var srText;

    if (btnState !== 'initial') {
      srText = screenReaderText || btnState;
    }

    var successIconSize = size === 'sm' ? 'sm' : 'md';
    var successIconCSSVal = [buttonStyles.successStateIcon, size === 'sm' && buttonStyles.successStateIconSm];
    var buttonCheckmarkIcon = jsx(CheckmarkIcon, {
      css: successIconCSSVal,
      size: successIconSize,
      "data-ppui": "true"
    });
    var iconMarkup = null;

    if (iconComponent) {
      var svgIconSize = size === 'sm' ? 'xs' : 'sm';
      iconMarkup = jsx(iconComponent, {
        css: buttonStyles.icon,
        size: svgIconSize,
        "data-ppui": "true"
      });
    }

    var overlayCSSVal = [buttonStyles.overlay, btnState === 'success' && buttonStyles.successStateOverlay];
    var stateOverlay = btnState !== 'initial' ? jsx("span", {
      css: overlayCSSVal,
      "data-ppui": "true"
    }, btnState === 'success' && buttonCheckmarkIcon, btnState === 'processing' && jsx(LoadingSpinner, _extends({}, loadingSpinnerProps, {
      "data-ppui": "true"
    }))) : null;
    var assistiveText = srText ? jsx("span", {
      role: "status",
      css: buttonStyles.screenReader,
      "data-ppui": "true"
    }, children, " ", srText) : null;
    return jsx(React.Fragment, null, iconMarkup, children, jsx(AffordanceIcon, {
      isMenu: isMenu,
      isOpen: isOpen,
      isSmall: size === 'sm',
      css: buttonStyles.affordance,
      "data-ppui": "true"
    }), stateOverlay, assistiveText);
  }());
});

var _excluded$1 = ["id", "name", "onSelection", "options", "children", "mobileTitle", "onClose"];
var ButtonMenu = function (props) {
  var id = props.id,
      name = props.name,
      onSelection = props.onSelection,
      options = props.options,
      children = props.children,
      _props$mobileTitle = props.mobileTitle,
      mobileTitle = _props$mobileTitle === void 0 ? name : _props$mobileTitle,
      onClose = props.onClose,
      rest = _objectWithoutProperties(props, _excluded$1);

  var _useState = useState(false),
      _useState2 = _slicedToArray(_useState, 2),
      isMenuOpen = _useState2[0],
      setIsMenuOpen = _useState2[1];

  var menuId = "".concat(id, "_menu");
  var buttonRef = useRef(null);
  var buttonStyles = styles(useTheme());
  return jsx("div", {
    css: buttonStyles.menuWrapper,
    "data-ppui": "true"
  }, jsx(Button, _extends({
    onClick: function toggleMenu() {
      setIsMenuOpen(function (currentlyOpen) {
        return !currentlyOpen;
      });
    },
    isMenu: true,
    isOpen: isMenuOpen,
    "aria-haspopup": menuId,
    "aria-expanded": isMenuOpen,
    id: id,
    ref: buttonRef
  }, rest, {
    "data-ppui": "true"
  }), children), jsx(ActionMenu, _extends({
    name: name,
    id: menuId,
    initialItemFocus: true,
    triggerRef: buttonRef,
    isOpen: isMenuOpen,
    options: options,
    mobileTitle: mobileTitle,
    onClose: function handleClose(evt) {
      setIsMenuOpen(false);
      /* istanbul ignore next */

      if (typeof onClose === 'function') {
        onClose(evt);
      }
    },
    onSelection: function handleSelection(index) {
      setIsMenuOpen(false);
      /* istanbul ignore next */

      if (typeof onSelection === 'function') {
        onSelection(index);
      }

      buttonRef.current.focus();
    }
  }, rest, {
    "data-ppui": "true"
  })));
};

var ButtonGroup = function (_ref) {
  var className = _ref.className,
      _ref$fullWidthOnMobil = _ref.fullWidthOnMobile,
      fullWidthOnMobile = _ref$fullWidthOnMobil === void 0 ? true : _ref$fullWidthOnMobil,
      children = _ref.children;
  // If theme is not directly provided, get from themeContext
  var tokens = useTheme();

  var _styles = useMemo(function () {
    return styles(tokens);
  }, [tokens]);

  var childArray = Children.toArray(children); //@ts-ignore

  var breakPointValue = "".concat(stripUnit(remToPx(tokens.sysSizeMediaSm)));
  var innerWidth = hasWindow() && window.innerWidth;
  var computedChildren = children;

  if (breakPointValue && innerWidth > parseInt(breakPointValue, 10)) {
    computedChildren = childArray.reverse();
  }

  var hasButtonCountOne = childArray.length === 1;
  var buttonGroupCSSVal = [_styles.buttonGroup, hasButtonCountOne && _styles.buttonGroupSolo, fullWidthOnMobile && _styles.btnGroupFullWidth];
  return jsx("div", {
    css: buttonGroupCSSVal,
    className: className,
    "data-ppui": "true"
  }, computedChildren);
};

export { Button, ButtonGroup, ButtonMenu, styles as buttonStyles };
